import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, apiErrorMessage } from "../lib/api";
import { toast } from "sonner";
import { TID } from "../lib/testIds";
import { Send, MessageSquare } from "lucide-react";

export default function TestProviderDialog({ open, onOpenChange, provider }) {
  const [model, setModel] = useState("");
  const [prompt, setPrompt] = useState("Hola, ¿qué modelo eres?");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open && provider) {
      setModel(provider.models?.[0] || "");
      setResult(null);
    }
  }, [open, provider]);

  async function send(e) {
    e.preventDefault();
    if (!model.trim() || !prompt.trim()) return;
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post(
        `/providers/${encodeURIComponent(provider.id)}/chat`,
        {
          model,
          messages: [{ role: "user", content: prompt }],
          stream: false,
        }
      );
      setResult(data);
      toast.success("Respuesta recibida");
    } catch (err) {
      setResult({ error: apiErrorMessage(err) });
      toast.error(apiErrorMessage(err));
    } finally {
      setRunning(false);
    }
  }

  if (!provider) return null;

  const responseText =
    result?.choices?.[0]?.message?.content ||
    result?.response ||
    (result?.error ? "" : JSON.stringify(result, null, 2));
  const usage = result?.usage;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={TID.providers.chatDialog}
        className="bg-zinc-950 border border-zinc-800 text-zinc-100 sm:max-w-[640px] max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Probar {provider.name}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Envía un prompt de prueba (OpenAI-compatible).{" "}
            <code className="font-mono text-zinc-300">
              POST /api/providers/{provider.id}/chat
            </code>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={send} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-zinc-300">Modelo</Label>
            {provider.models && provider.models.length > 0 ? (
              <Select value={model} onValueChange={setModel}>
                <SelectTrigger
                  data-testid={TID.providers.chatModel}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono text-sm"
                >
                  <SelectValue placeholder="Selecciona modelo" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-72">
                  {provider.models.map((m) => (
                    <SelectItem key={m} value={m} className="font-mono text-xs focus:bg-zinc-900">
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="text-xs text-amber-400">
                Este proveedor no tiene modelos configurados aún.
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-300">Prompt</Label>
            <Textarea
              data-testid={TID.providers.chatPrompt}
              value={prompt} onChange={(e) => setPrompt(e.target.value)}
              rows={3}
              className="bg-zinc-900 border-zinc-800 text-sm"
              placeholder="Escribe tu mensaje…"
            />
          </div>

          {result && (
            <div
              data-testid={TID.providers.chatResult}
              className="rounded-md border border-zinc-800 bg-black/40 p-4 text-xs space-y-2"
            >
              {result.error ? (
                <div className="text-red-400 font-mono whitespace-pre-wrap">
                  {result.error}
                </div>
              ) : (
                <>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono flex items-center justify-between">
                    <span>Respuesta</span>
                    {usage && (
                      <span>
                        tokens: {usage.prompt_tokens ?? "?"}→{usage.completion_tokens ?? "?"}
                      </span>
                    )}
                  </div>
                  <div className="text-zinc-200 whitespace-pre-wrap leading-relaxed max-h-[40vh] overflow-y-auto">
                    {responseText || "(vacío)"}
                  </div>
                </>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}
              className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800">
              Cerrar
            </Button>
            <Button type="submit" data-testid={TID.providers.chatSend}
              disabled={running || !model || !prompt.trim()}
              className="bg-white text-black hover:bg-zinc-200">
              <Send className="h-3.5 w-3.5 mr-1.5" />
              {running ? "Enviando…" : "Enviar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
